"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface StickyContinueBarContent {
  action: ReactNode;
  // The extra area above the action button - the clear-cart button on the
  // cart page, the order-summary toggle on the payment page. `id` identifies
  // which one it is, so the bar can tell a genuine appear/disappear (grow or
  // shrink the container) apart from swapping directly between two of these
  // (cart <-> payment with no page in between, which should just crossfade).
  secondary?: { id: string; content: ReactNode };
  // Rendered as a sibling BEFORE the bar's own Card, not nested inside it.
  // The Card is a `fixed` + z-indexed element, so it establishes its own
  // stacking context - a backdrop nested inside it would only compete with
  // its local siblings (the action button, etc.) for stacking order rather
  // than sitting below the Card the way it's meant to, regardless of z-index
  // values, and would visually cover the bar's own content.
  backdrop?: ReactNode;
}

interface StickyContinueBarContentValue {
  content: StickyContinueBarContent | null;
  // Tracked in the (always-mounted) Provider rather than in the bar
  // component itself, since content briefly goes null between pages (e.g.
  // while the payment page's own data is still loading) which unmounts the
  // bar and would otherwise reset this, breaking the animation's ability to
  // tell an enter/exit/swap apart across navigation.
  prevSecondaryIdRef: React.RefObject<string | null>;
}

// Split into two contexts on purpose: pages that call useSetStickyContinueBar
// must only ever subscribe to the setter (stable across renders, from
// useState), never to the content value itself. If they read both from one
// combined context, registering content triggers a re-render of every
// consumer of that context - including the page that just registered it -
// which registers new content again and loops forever.
const StickyContinueBarContentContext =
  createContext<StickyContinueBarContentValue | null>(null);
const StickyContinueBarSetterContext = createContext<
  ((content: StickyContinueBarContent | null) => void) | null
>(null);

export function StickyContinueBarProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [content, setContent] = useState<StickyContinueBarContent | null>(null);
  const prevSecondaryIdRef = useRef<string | null>(null);

  useEffect(() => {
    prevSecondaryIdRef.current = content?.secondary?.id ?? null;
  }, [content]);

  return (
    <StickyContinueBarSetterContext.Provider value={setContent}>
      <StickyContinueBarContentContext.Provider
        value={{ content, prevSecondaryIdRef }}
      >
        {children}
      </StickyContinueBarContentContext.Provider>
    </StickyContinueBarSetterContext.Provider>
  );
}

// Read-only hook for the bar component itself.
export function useStickyContinueBarContent() {
  const value = useContext(StickyContinueBarContentContext);
  const setContent = useContext(StickyContinueBarSetterContext);
  if (setContent === null || value === null) {
    throw new Error(
      "useStickyContinueBarContent must be used within a StickyContinueBarProvider"
    );
  }
  return value;
}

// A page calls this to register what the persistent sticky bar should show
// while it's mounted. Content updates on every render (it's usually fresh
// JSX each time) and is cleared only when the calling page unmounts. Only
// touches the setter context, so registering content never re-renders the
// page that's registering it.
export function useSetStickyContinueBar(
  content: StickyContinueBarContent | null
) {
  const setContent = useContext(StickyContinueBarSetterContext);
  if (setContent === null) {
    throw new Error(
      "useSetStickyContinueBar must be used within a StickyContinueBarProvider"
    );
  }

  useEffect(() => {
    setContent(content);
  });

  // Separate effect with an empty dependency array so this cleanup only
  // fires on unmount, not between every re-render of the calling page.
  useEffect(() => {
    return () => setContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
